import React, { useState, useEffect, useCallback } from 'react';
import { transfersApi, sitesApi, entitiesApi, partnerAccountsApi, entityBankAccountsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, ArrowRightLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Landmark, Banknote, CreditCard, Building2, PlusCircle, X, Check, UserCheck, Trash2
} from 'lucide-react';

const TRANSFER_TYPES = [
  { value: 'entry_change', label: 'Entry Change', desc: 'Cash to partner, returned via bank' },
  { value: 'dummy_account_route', label: 'Dummy Account Route', desc: 'Customer pays via partner dummy account' },
  { value: 'atm_withdrawal', label: 'ATM / Cash Withdrawal', desc: 'Withdraw cash from bank' },
];

const MODE_OPTIONS = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'bank', label: 'Company Bank', icon: Landmark },
  { value: 'partner_account', label: 'Partner Account', icon: CreditCard },
  { value: 'customer', label: 'Customer (Direct)', icon: UserCheck },
];

const MODE_ICONS = { cash: Banknote, bank: Landmark, partner_account: CreditCard, customer: UserCheck, upi: CreditCard };
const MODE_LABELS = { cash: 'Cash', bank: 'Company Bank', partner_account: 'Partner Account', customer: 'Customer (Direct)', upi: 'UPI' };

export default function TransfersPage() {
  const { user } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [siteMap, setSiteMap] = useState({});
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [partnerAccounts, setPartnerAccounts] = useState([]);
  const [allEntityBankAccounts, setAllEntityBankAccounts] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterSite, setFilterSite] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('entry_change');
  const [formSite, setFormSite] = useState('');
  const [formPartner, setFormPartner] = useState('');
  const [formCustomer, setFormCustomer] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formSteps, setFormSteps] = useState([{
    date: new Date().toISOString().slice(0, 10),
    description: '', from_mode: 'cash', from_account_id: '',
    to_mode: 'bank', to_account_id: '', amount: '', notes: ''
  }]);

  // Add step dialog
  const [showAddStep, setShowAddStep] = useState(null); // transfer_id
  const [newStep, setNewStep] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: '', from_mode: 'cash', from_account_id: '',
    to_mode: 'bank', to_account_id: '', amount: '', notes: ''
  });

  // Partner account management
  const [showAccounts, setShowAccounts] = useState(false);
  const [newAccName, setNewAccName] = useState('');
  const [newAccBank, setNewAccBank] = useState('');
  const [newAccNum, setNewAccNum] = useState('');
  const [newAccPartner, setNewAccPartner] = useState('');

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filterType) params.transfer_type = filterType;
      if (filterSite) params.site_id = filterSite;
      const { data } = await transfersApi.list(params);
      setTransfers(data.transfers);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { toast.error('Failed to load transfers'); }
    setLoading(false);
  }, [page, filterType, filterSite]);

  useEffect(() => {
    sitesApi.list().then(r => {
      setSites(r.data);
      const map = {};
      r.data.forEach(s => { map[s._id] = s.name; });
      setSiteMap(map);
    }).catch(() => {});
    entitiesApi.list('partner').then(r => setPartners(r.data)).catch(() => {});
    entitiesApi.list('customer').then(r => setCustomers(r.data)).catch(() => {});
    partnerAccountsApi.list().then(r => setPartnerAccounts(r.data)).catch(() => {});
    entityBankAccountsApi.listAll().then(r => setAllEntityBankAccounts(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const resetForm = () => {
    setFormType('entry_change');
    setFormSite('');
    setFormPartner('');
    setFormCustomer('');
    setFormDesc('');
    setFormSteps([{
      _key: Math.random().toString(36).slice(2),
      date: new Date().toISOString().slice(0, 10),
      description: '', from_mode: 'cash', from_account_id: '',
      to_mode: 'bank', to_account_id: '', amount: '', payment_mode: 'cash', notes: ''
    }]);
  };

  // Set default modes based on transfer type
  const handleTypeChange = (type) => {
    setFormType(type);
    let defaultSteps;
    if (type === 'atm_withdrawal') {
      defaultSteps = [{ _key: Math.random().toString(36).slice(2), date: new Date().toISOString().slice(0, 10), description: 'ATM Cash Withdrawal', from_mode: 'bank', from_account_id: '', to_mode: 'cash', to_account_id: '', amount: '', payment_mode: 'bank', notes: '' }];
    } else if (type === 'entry_change') {
      defaultSteps = [
        { _key: Math.random().toString(36).slice(2), date: new Date().toISOString().slice(0, 10), description: 'Cash given to partner', from_mode: 'cash', from_account_id: '', to_mode: 'partner_account', to_account_id: '', amount: '', payment_mode: 'cash', notes: '' },
        { _key: Math.random().toString(36).slice(2), date: '', description: 'Partner returns via bank', from_mode: 'partner_account', from_account_id: '', to_mode: 'bank', to_account_id: '', amount: '', payment_mode: 'bank', notes: '' },
      ];
    } else if (type === 'dummy_account_route') {
      defaultSteps = [
        { _key: Math.random().toString(36).slice(2), date: new Date().toISOString().slice(0, 10), description: 'Customer pays into partner dummy account', from_mode: 'customer', from_account_id: '', to_mode: 'partner_account', to_account_id: '', amount: '', payment_mode: 'cash', notes: '' },
        { _key: Math.random().toString(36).slice(2), date: '', description: 'Partner transfers to company', from_mode: 'partner_account', from_account_id: '', to_mode: 'bank', to_account_id: '', amount: '', payment_mode: 'bank', notes: '' },
      ];
    }
    setFormSteps(defaultSteps);
  };

  const updateStep = (idx, field, value) => {
    setFormSteps(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addFormStep = () => {
    setFormSteps(prev => [...prev, {
      _key: Math.random().toString(36).slice(2),
      date: '', description: '', from_mode: 'cash', from_account_id: '',
      to_mode: 'bank', to_account_id: '', amount: '', payment_mode: 'cash', notes: ''
    }]);
  };

  const removeFormStep = (idx) => {
    if (formSteps.length <= 1) return;
    setFormSteps(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const steps = formSteps.filter(s => s.date && s.amount).map(s => {
      const { _key, ...rest } = s;
      return { ...rest, amount: parseFloat(rest.amount) };
    });
    if (steps.length === 0) return;
    try {
      await transfersApi.create({
        transfer_type: formType,
        site_id: formSite,
        description: formDesc,
        partner_id: formPartner || null,
        customer_id: formCustomer || null,
        steps
      });
      setShowForm(false);
      resetForm();
      fetchTransfers();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create transfer'); }
  };

  const handleAddStep = async () => {
    if (!showAddStep || !newStep.date || !newStep.amount) return;
    try {
      await transfersApi.addStep(showAddStep, {
        ...newStep,
        amount: parseFloat(newStep.amount)
      });
      setShowAddStep(null);
      setNewStep({
        date: new Date().toISOString().slice(0, 10),
        description: '', from_mode: 'cash', from_account_id: '',
        to_mode: 'bank', to_account_id: '', amount: '', notes: ''
      });
      fetchTransfers();
    } catch (err) { toast.error('Failed to add step'); }
  };

  const handleCompleteTransfer = async (transferId) => {
    try {
      await transfersApi.complete(transferId);
      fetchTransfers();
    } catch (err) { toast.error('Failed to complete transfer'); }
  };

  const handleCreateAccount = async () => {
    if (!newAccName.trim() || !newAccPartner) return;
    try {
      await partnerAccountsApi.create({
        partner_id: newAccPartner,
        account_name: newAccName.trim(),
        bank_name: newAccBank,
        account_number: newAccNum
      });
      const { data } = await partnerAccountsApi.list();
      setPartnerAccounts(data);
      entityBankAccountsApi.listAll().then(r => setAllEntityBankAccounts(r.data)).catch(() => {});
      setNewAccName('');
      setNewAccBank('');
      setNewAccNum('');
    } catch (err) { toast.error('Failed to create account'); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this dummy account?')) return;
    try {
      await partnerAccountsApi.delete(id);
      const { data } = await partnerAccountsApi.list();
      setPartnerAccounts(data);
      entityBankAccountsApi.listAll().then(r => setAllEntityBankAccounts(r.data)).catch(() => {});
    } catch (err) { toast.error('Failed to delete account'); }
  };

  const handleDeleteTransfer = async (transferId) => {
    if (!window.confirm('Delete this transfer and all its associated entries? This action moves everything to trash.')) return;
    try {
      await transfersApi.delete(transferId);
      toast.success('Transfer deleted');
      setExpandedId(null);
      fetchTransfers();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete transfer'); }
  };

  const partnerAccountsForPartner = (pid) => {
    const pAccs = partnerAccounts.filter(a => a.partner_id === pid);
    const eAccs = allEntityBankAccounts
      .filter(a => a.entity_id === pid)
      .map(a => ({ ...a, _source: 'entity_bank' }));
    return [...pAccs, ...eAccs];
  };

  const ModeIcon = ({ mode }) => {
    const Icon = MODE_ICONS[mode] || CreditCard;
    return <Icon className="w-3.5 h-3.5" />;
  };

  const renderModeSelect = (value, onChange, partnerId) => {
    const accs = partnerAccountsForPartner(partnerId || formPartner);
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Select mode" />
        </SelectTrigger>
        <SelectContent>
          {MODE_OPTIONS.map(m => (
            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderAccountSelect = (value, onChange, partnerId) => {
    const accs = partnerAccountsForPartner(partnerId || formPartner);
    if (accs.length === 0) return <p className="text-xs text-muted-foreground mt-1">No dummy accounts. Add from Manage Accounts.</p>;
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs mt-1">
          <SelectValue placeholder="Select account" />
        </SelectTrigger>
        <SelectContent>
          {accs.map(a => (
            <SelectItem key={a._id} value={a._id}>{a.account_name}{a.bank_name ? ` (${a.bank_name})` : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="space-y-6" data-testid="transfers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Transfers</h1>
          <p className="text-sm text-muted-foreground mt-1">Entry changes, dummy account routes & internal transfers</p>
        </div>
        {user?.role === 'admin' && (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-1.5" onClick={() => setShowAccounts(true)} data-testid="manage-accounts-btn">
              <CreditCard className="w-4 h-4" /> Manage Accounts
            </Button>
            <Button className="gap-1.5" onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-transfer-btn">
              <Plus className="w-4 h-4" /> New Transfer
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <ArrowRightLeft className="w-4 h-4 text-muted-foreground" />
        <Select value={filterType} onValueChange={v => { setFilterType(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="filter-transfer-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TRANSFER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSite} onValueChange={v => { setFilterSite(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-transfer-site">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sites</SelectItem>
            {sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground ml-2">{total} transfers</span>
      </div>

      {/* Transfers List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : transfers.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center">
          <ArrowRightLeft className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-40" />
          <h3 className="font-semibold mb-1">No transfers yet</h3>
          <p className="text-sm text-muted-foreground">Create your first transfer to track entry changes and internal fund movements</p>
        </div>
      ) : (
        <div className="space-y-3">
          {transfers.map(t => {
            const isExpanded = expandedId === t.transfer_id;
            const typeInfo = TRANSFER_TYPES.find(tt => tt.value === t.transfer_type);
            return (
              <div key={t.transfer_id} className="bg-card border border-border" data-testid={`transfer-${t.transfer_id}`}>
                {/* Header Row */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : t.transfer_id)}
                  data-testid={`transfer-toggle-${t.transfer_id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="p-2 bg-muted rounded shrink-0">
                      <ArrowRightLeft className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{typeInfo?.label || t.transfer_type}</span>
                        <span className={`text-xs px-1.5 py-0.5 ${t.status === 'completed' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400' : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400'}`}>
                          {t.status === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex gap-2 flex-wrap">
                        <span>{t.site_name}</span>
                        {t.partner_name && <span>| {t.partner_name}</span>}
                        {t.customer_name && <span>| Customer: {t.customer_name}</span>}
                        <span>| {t.steps?.length || 0} steps</span>
                        <span>| {t.created_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono font-bold text-sm">{'\u20B9'}{(t.total_amount || 0).toLocaleString('en-IN')}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </div>

                {/* Expanded Timeline */}
                {isExpanded && (
                  <div className="border-t border-border p-4">
                    {t.description && <p className="text-sm text-muted-foreground mb-4">{t.description}</p>}
                    {/* Timeline */}
                    <div className="relative pl-6 space-y-4">
                      <div className="absolute left-2.5 top-1 bottom-1 w-px bg-border" />
                      {(t.steps || []).map((step) => (
                        <div key={step.step_num} className="relative" data-testid={`transfer-step-${t.transfer_id}-${step.step_num}`}>
                          <div className="absolute -left-3.5 top-1.5 w-3 h-3 rounded-full border-2 border-primary bg-background z-10" />
                          <div className="bg-muted/50 p-3 ml-2">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Step {step.step_num}</span>
                                <span className="text-xs text-muted-foreground">{step.date}</span>
                              </div>
                              <span className="font-mono text-sm font-medium">{'\u20B9'}{(step.amount || 0).toLocaleString('en-IN')}</span>
                            </div>
                            <p className="text-sm mt-1">{step.description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground flex-wrap">
                              <div className="flex items-center gap-1">
                                <ModeIcon mode={step.from_mode} />
                                <span>{MODE_LABELS[step.from_mode] || step.from_mode}</span>
                                {step.from_account_name && <span className="text-foreground font-medium">({step.from_account_name})</span>}
                              </div>
                              <ArrowRightLeft className="w-3 h-3" />
                              <div className="flex items-center gap-1">
                                <ModeIcon mode={step.to_mode} />
                                <span>{MODE_LABELS[step.to_mode] || step.to_mode}</span>
                                {step.to_account_name && <span className="text-foreground font-medium">({step.to_account_name})</span>}
                              </div>
                            </div>
                            {step.notes && <p className="text-xs text-muted-foreground mt-1">{step.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Actions */}
                    {user?.role === 'admin' && (
                      <div className="flex gap-2 mt-4 pt-3 border-t border-border">
                        <Button
                          variant="outline" size="sm" className="gap-1"
                          onClick={(e) => { e.stopPropagation(); setShowAddStep(t.transfer_id); }}
                          data-testid={`add-step-${t.transfer_id}`}
                        >
                          <PlusCircle className="w-3.5 h-3.5" /> Add Step
                        </Button>
                        {t.status !== 'completed' && (
                          <Button
                            variant="outline" size="sm" className="gap-1 text-emerald-700 hover:text-emerald-700"
                            onClick={(e) => { e.stopPropagation(); handleCompleteTransfer(t.transfer_id); }}
                            data-testid={`complete-transfer-${t.transfer_id}`}
                          >
                            <Check className="w-3.5 h-3.5" /> Mark Complete
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm" className="gap-1 text-red-600 hover:text-red-600 ml-auto"
                          onClick={(e) => { e.stopPropagation(); handleDeleteTransfer(t.transfer_id); }}
                          data-testid={`delete-transfer-${t.transfer_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* ==================== NEW TRANSFER DIALOG ==================== */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Transfer</DialogTitle>
            <DialogDescription className="sr-only">Create a new transfer with multiple steps</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="transfer-form">
            {/* Transfer Type */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Transfer Type</Label>
              <Select value={formType} onValueChange={handleTypeChange}>
                <SelectTrigger className="mt-1.5" data-testid="transfer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TRANSFER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <span>{t.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {TRANSFER_TYPES.find(t => t.value === formType)?.desc}
              </p>
            </div>

            {/* Site + Partner + Customer */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Site</Label>
                <Select value={formSite} onValueChange={setFormSite}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent>{sites.map(s => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(formType === 'entry_change' || formType === 'dummy_account_route') && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Partner</Label>
                  <Select value={formPartner} onValueChange={setFormPartner}>
                    <SelectTrigger className="mt-1.5" data-testid="transfer-partner-select"><SelectValue placeholder="Select partner" /></SelectTrigger>
                    <SelectContent>{partners.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {formType === 'dummy_account_route' && (
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Customer</Label>
                <Select value={formCustomer} onValueChange={setFormCustomer}>
                  <SelectTrigger className="mt-1.5" data-testid="transfer-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">The customer who is paying into the partner's dummy account</p>
              </div>
            )}

            {/* Description */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Description (optional)</Label>
              <Input value={formDesc} onChange={e => setFormDesc(e.target.value)} placeholder="Brief description..." className="mt-1.5" data-testid="transfer-desc-input" />
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Timeline Steps</Label>
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addFormStep} data-testid="add-form-step-btn">
                  <PlusCircle className="w-3.5 h-3.5" /> Add Step
                </Button>
              </div>
              <div className="space-y-3">
                {formSteps.map((step, idx) => (
                  <div key={step._key || idx} className="border border-border p-3 space-y-3 relative" data-testid={`form-step-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Step {idx + 1}</span>
                      {formSteps.length > 1 && (
                        <button type="button" onClick={() => removeFormStep(idx)} className="p-1 hover:bg-muted rounded text-muted-foreground">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Date</Label>
                        <Input type="date" value={step.date} onChange={e => updateStep(idx, 'date', e.target.value)} className="mt-1 h-8 text-xs" required data-testid={`step-date-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Amount ({'\u20B9'})</Label>
                        <Input type="number" step="0.01" min="0" value={step.amount} onChange={e => updateStep(idx, 'amount', e.target.value)} className="mt-1 h-8 text-xs font-mono" required data-testid={`step-amount-${idx}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">Payment Mode</Label>
                        {formType === 'atm_withdrawal' ? (
                          <div className="mt-1 h-8 flex items-center px-3 border rounded-md bg-muted text-xs" data-testid={`step-payment-mode-${idx}`}>
                            <Landmark className="w-3.5 h-3.5 mr-1.5" /> Bank
                          </div>
                        ) : (
                          <Select value={step.payment_mode || 'cash'} onValueChange={v => updateStep(idx, 'payment_mode', v)}>
                            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`step-payment-mode-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash"><span className="flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5" /> Cash</span></SelectItem>
                              <SelectItem value="bank"><span className="flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5" /> Bank</span></SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Description</Label>
                        <Input value={step.description} onChange={e => updateStep(idx, 'description', e.target.value)} className="mt-1 h-8 text-xs" placeholder="What happens..." data-testid={`step-desc-${idx}`} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">From</Label>
                        {renderModeSelect(step.from_mode, v => updateStep(idx, 'from_mode', v), formPartner)}
                        {step.from_mode === 'partner_account' && renderAccountSelect(step.from_account_id, v => updateStep(idx, 'from_account_id', v), formPartner)}
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">To</Label>
                        {renderModeSelect(step.to_mode, v => updateStep(idx, 'to_mode', v), formPartner)}
                        {step.to_mode === 'partner_account' && renderAccountSelect(step.to_account_id, v => updateStep(idx, 'to_account_id', v), formPartner)}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Notes</Label>
                      <Input value={step.notes} onChange={e => updateStep(idx, 'notes', e.target.value)} className="mt-1 h-8 text-xs" placeholder="Optional notes..." data-testid={`step-notes-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button type="submit" className="w-full" data-testid="submit-transfer-btn">Create Transfer</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== ADD STEP DIALOG ==================== */}
      <Dialog open={!!showAddStep} onOpenChange={() => setShowAddStep(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Step to Transfer</DialogTitle>
            <DialogDescription className="sr-only">Add a new step to an existing transfer</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="add-step-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input type="date" value={newStep.date} onChange={e => setNewStep(s => ({ ...s, date: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="new-step-date" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Amount</Label>
                <Input type="number" step="0.01" min="0" value={newStep.amount} onChange={e => setNewStep(s => ({ ...s, amount: e.target.value }))} className="mt-1 h-8 text-xs font-mono" data-testid="new-step-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input value={newStep.description} onChange={e => setNewStep(s => ({ ...s, description: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="new-step-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">From</Label>
                {renderModeSelect(newStep.from_mode, v => setNewStep(s => ({ ...s, from_mode: v })))}
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">To</Label>
                {renderModeSelect(newStep.to_mode, v => setNewStep(s => ({ ...s, to_mode: v })))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Input value={newStep.notes} onChange={e => setNewStep(s => ({ ...s, notes: e.target.value }))} className="mt-1 h-8 text-xs" data-testid="new-step-notes" />
            </div>
            <Button onClick={handleAddStep} className="w-full" data-testid="confirm-add-step-btn">Add Step</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== MANAGE PARTNER ACCOUNTS DIALOG ==================== */}
      <Dialog open={showAccounts} onOpenChange={setShowAccounts}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Partner Dummy Accounts</DialogTitle>
            <DialogDescription className="sr-only">Manage dummy bank accounts for partners</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="partner-accounts-dialog">
            {/* Create new */}
            <div className="border border-border p-3 space-y-3">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Add New Account</Label>
              <div>
                <Label className="text-xs text-muted-foreground">Partner</Label>
                <Select value={newAccPartner} onValueChange={setNewAccPartner}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid="new-acc-partner">
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>{partners.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Account Name</Label>
                <Input value={newAccName} onChange={e => setNewAccName(e.target.value)} placeholder="e.g., Rakesh SBI Personal" className="mt-1 h-8 text-xs" data-testid="new-acc-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Bank Name</Label>
                  <Input value={newAccBank} onChange={e => setNewAccBank(e.target.value)} placeholder="SBI, HDFC..." className="mt-1 h-8 text-xs" data-testid="new-acc-bank" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Account Number</Label>
                  <Input value={newAccNum} onChange={e => setNewAccNum(e.target.value)} placeholder="Optional" className="mt-1 h-8 text-xs" data-testid="new-acc-number" />
                </div>
              </div>
              <Button size="sm" onClick={handleCreateAccount} data-testid="save-new-account-btn">Add Account</Button>
            </div>

            {/* List */}
            {partnerAccounts.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-4">No dummy accounts created yet</p>
            ) : (
              <div className="space-y-2">
                {partners.filter(p => partnerAccountsForPartner(p._id).length > 0).map(partner => (
                  <div key={partner._id}>
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{partner.name}</div>
                    {partnerAccountsForPartner(partner._id).map(acc => (
                      <div key={acc._id} className="flex items-center justify-between border border-border p-2" data-testid={`account-${acc._id}`}>
                        <div>
                          <span className="text-sm font-medium">{acc.account_name}</span>
                          {acc.bank_name && <span className="text-xs text-muted-foreground ml-2">{acc.bank_name}</span>}
                          {acc.account_number && <span className="text-xs text-muted-foreground ml-1">({acc.account_number})</span>}
                        </div>
                        <button onClick={() => handleDeleteAccount(acc._id)} className="p-1 hover:bg-muted rounded text-red-600" data-testid={`delete-account-${acc._id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
